import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import {
  Tv as ChannelsIcon,
  VideoLibrary as ShowsIcon,
  VideoLibrary as EpisodesIcon,
  People as PanelistsIcon,
  Search as NewsIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { apiService, Channel, Show, Episode, Panelist } from '../services/api';

const Dashboard: React.FC = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsData, showsData, episodesData, panelistsData] = await Promise.all([
          apiService.getChannels(),
          apiService.getShows(),
          apiService.getEpisodes(),
          apiService.getPanelists(),
        ]);
        setChannels(channelsData);
        setShows(showsData);
        setEpisodes(episodesData);
        setPanelists(panelistsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
            mb: 1,
          }}
        >
          Welcome to iNews Studio
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontWeight: 400,
            maxWidth: '500px',
            lineHeight: 1.5,
          }}
        >
          Manage your channels, shows, episodes, and content creation workflow
        </Typography>
      </Box>
      
      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card 
            className="slide-up"
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <ChannelsIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800' }}>
                  Channels
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6', mb: 2 }}>
                {channels.length}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/channels')}
                fullWidth
                size="small"
                sx={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: 1.5,
                  py: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Manage Channels
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card 
            className="slide-up"
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <ShowsIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800' }}>
                  Shows
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981', mb: 2 }}>
                {shows.length}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/channels')}
                fullWidth
                size="small"
                sx={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: 1.5,
                  py: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Manage Shows
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card 
            className="slide-up"
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <EpisodesIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.800' }}>
                  Episodes
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b', mb: 3 }}>
                {episodes.length}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/channels')}
                fullWidth
                sx={{ 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: 2,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Manage Episodes
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} sm={6} md={3}>
          <Card 
            className="slide-up"
            sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <PanelistsIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.800' }}>
                  Panelists
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#8b5cf6', mb: 3 }}>
                {panelists.length}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/panelists')}
                fullWidth
                sx={{ 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  borderRadius: 2,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Manage Panelists
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Content */}
      <Grid container spacing={3}>
        {/* Recent Channels */}
        <Grid xs={12} md={6}>
          <Card 
            className="slide-up"
            sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e2e8f0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'grey.800', mb: 3 }}>
                Recent Channels
              </Typography>
              {channels.slice(0, 5).map((channel) => (
                <Box 
                  key={channel._id} 
                  sx={{ 
                    mb: 2, 
                    p: 2.5, 
                    border: '1px solid #e2e8f0', 
                    borderRadius: 2,
                    backgroundColor: '#fafbfc',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#f8fafc',
                      borderColor: '#cbd5e1',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800' }}>
                      {channel.name}
                    </Typography>
                    <Chip 
                      label={channel.status} 
                      color={getStatusColor(channel.status) as any}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {channel.description || 'No description'}
                  </Typography>
                </Box>
              ))}
              {channels.length === 0 && (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No channels created yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Shows */}
        <Grid xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'grey.800' }}>
                Recent Shows
              </Typography>
              {shows.slice(0, 5).map((show) => (
                <Box key={show._id} sx={{ mb: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {show.name}
                    </Typography>
                    <Chip 
                      label={show.status} 
                      color={getStatusColor(show.status) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {show.description || 'No description'}
                  </Typography>
                  {show.frequency && <Chip label={show.frequency} size="small" sx={{ mr: 1 }} />}
                  {show.category && <Chip label={show.category} size="small" />}
                </Box>
              ))}
              {shows.length === 0 && (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No shows created yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Episodes */}
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'grey.800' }}>
                Recent Episodes
              </Typography>
              <Grid container spacing={2}>
                {episodes.slice(0, 6).map((episode) => (
                  <Grid xs={12} sm={6} md={4} key={episode._id}>
                    <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {episode.title}
                        </Typography>
                        <Chip 
                          label={episode.status} 
                          color={getStatusColor(episode.status) as any}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {episode.description || 'No description'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {episode.episodeNumber && (
                          <Chip label={`Ep ${episode.episodeNumber}`} size="small" />
                        )}
                        {episode.duration && (
                          <Chip label={`${episode.duration}`} size="small" />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {episodes.length === 0 && (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No episodes created yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
